import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { PlayArrow as PlayIcon, Stop as StopIcon, Delete as DeleteIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { schedulerApi, backgroundProcessApi, monitorChannelApi, MonitorChannel } from '../services/api';
import { SchedulerConfig, BackgroundProcess } from '../types';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { formatAppDateTime } from '../utils/dateFormat';

const getDefaultStartDt = () => {
  const now = dayjs();
  return now.startOf('month').subtract(1, 'month').format('YYYY-MM-DD');
};

const getDefaultEndDt = () => {
  const now = dayjs();
  return now.startOf('month').format('YYYY-MM-DD');
};

const parseCommaSeparatedList = (value: string) =>
  value
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);

const TaskManagement: React.FC = () => {
  const [config, setConfig] = useState<SchedulerConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [runningProcesses, setRunningProcesses] = useState<BackgroundProcess[]>([]);
  const navigate = useNavigate();
  const [startDt, setStartDt] = useState(getDefaultStartDt());
  const [endDt, setEndDt] = useState(getDefaultEndDt());
  const [publishing, setPublishing] = useState(false);
  const [collectingVkStats, setCollectingVkStats] = useState(false);
  const [collectingYouTubeStats, setCollectingYouTubeStats] = useState(false);
  const [collectingRutubeStats, setCollectingRutubeStats] = useState(false);
  const [vkordPlatform, setVkordPlatform] = useState<string>('ALL');
  const [vkordErids, setVkordErids] = useState('');
  const [vkordExternalVideoIds, setVkordExternalVideoIds] = useState('');
  const [channels, setChannels] = useState<MonitorChannel[]>([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [addingChannel, setAddingChannel] = useState(false);
  const [syncingChannelId, setSyncingChannelId] = useState<number | null>(null);
  const [newChannelPlatform, setNewChannelPlatform] = useState<'YOUTUBE' | 'VK_VIDEO' | 'RUTUBE'>('YOUTUBE');
  const [newChannelId, setNewChannelId] = useState('');
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelPadId, setNewChannelPadId] = useState('');

  useEffect(() => {
    loadConfig();
    loadRunningProcesses();
    loadChannels();
  }, []);

  const loadChannels = async () => {
    try {
      setChannelsLoading(true);
      setChannels(await monitorChannelApi.list());
    } catch (err) {
      console.error('Error loading channels:', err);
    } finally {
      setChannelsLoading(false);
    }
  };

  const handleAddChannel = async () => {
    if (!newChannelId.trim()) {
      setError('Укажите канал: @handle, UC… или VK owner id');
      return;
    }
    try {
      setAddingChannel(true);
      setError(null);
      await monitorChannelApi.create({
        platform: newChannelPlatform,
        channelId: newChannelId.trim(),
        name: newChannelName.trim() || undefined,
        platformExternalId: newChannelPadId.trim() || undefined,
        enabled: true,
      });
      setNewChannelId('');
      setNewChannelName('');
      setNewChannelPadId('');
      setSuccess('Канал добавлен');
      setTimeout(() => setSuccess(null), 3000);
      await loadChannels();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Не удалось добавить канал');
    } finally {
      setAddingChannel(false);
    }
  };

  const handleSyncChannel = async (id: number) => {
    try {
      setSyncingChannelId(id);
      setError(null);
      const result = await monitorChannelApi.sync(id);
      setSuccess(`Сбор завершён: ${result.name}`);
      setTimeout(() => setSuccess(null), 4000);
      await loadRunningProcesses();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Ошибка сбора канала');
    } finally {
      setSyncingChannelId(null);
    }
  };

  const handleToggleChannel = async (ch: MonitorChannel) => {
    try {
      await monitorChannelApi.update(ch.id, { enabled: !ch.enabled });
      await loadChannels();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Не удалось обновить канал');
    }
  };

  const handleDeleteChannel = async (id: number) => {
    if (!window.confirm('Удалить канал из списка парсинга?')) return;
    try {
      await monitorChannelApi.remove(id);
      await loadChannels();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Не удалось удалить канал');
    }
  };

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await schedulerApi.getConfig();
      setConfig(response);
    } catch (err) {
      setError('Ошибка загрузки конфигурации');
      console.error('Error loading config:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRunningProcesses = async () => {
    try {
      const response = await backgroundProcessApi.getRunning();
      setRunningProcesses(response);
    } catch (err) {
      console.error('Error loading running processes:', err);
    }
  };

  const handleConfigChange = (field: keyof SchedulerConfig, value: any) => {
    if (config) {
      setConfig({ ...config, [field]: value });
    }
  };

  const saveConfig = async () => {
    if (!config) return;
    
    try {
      setSaving(true);
      await schedulerApi.updateConfig(config);
      setSuccess('Конфигурация сохранена');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Ошибка сохранения конфигурации');
      console.error('Error saving config:', err);
    } finally {
      setSaving(false);
    }
  };

  const startTask = async (taskType: 'vk-video-sync' | 'youtube-sync' | 'rutube-sync' | 'vkord-sync' | 'stats-collection') => {
    try {
      setError(null);
      switch (taskType) {
        case 'vk-video-sync':
          await schedulerApi.startVkVideoSync();
          break;
        case 'youtube-sync':
          await schedulerApi.startYoutubeSync();
          break;
        case 'rutube-sync':
          await schedulerApi.startRutubeSync();
          break;
        case 'vkord-sync':
          await schedulerApi.startVkordSync();
          break;
        case 'stats-collection':
          await schedulerApi.startStatsCollection();
          break;
      }
      setSuccess(`Задача ${taskType} выполнена`);
      setTimeout(() => setSuccess(null), 3000);
      
      // Обновляем список запущенных процессов
      setTimeout(() => {
        loadRunningProcesses();
      }, 1000);
    } catch (err) {
      setError(`Ошибка запуска задачи ${taskType}`);
      console.error('Error starting task:', err);
    }
  };

  const handleVkordSend = async () => {
    try {
      setPublishing(true);
      setError(null);
      // Преобразуем даты в формат dd.MM.yyyy
      const formattedStartDt = dayjs(startDt).format('DD.MM.YYYY');
      const formattedEndDt = dayjs(endDt).format('DD.MM.YYYY');
      await schedulerApi.startVkordSend({
        startDt: formattedStartDt,
        endDt: formattedEndDt,
        platform: vkordPlatform === 'ALL' ? null : vkordPlatform,
        erids: parseCommaSeparatedList(vkordErids),
        externalVideoIds: parseCommaSeparatedList(vkordExternalVideoIds),
      });
      setSuccess('Публикация статистики в VK Ord запущена');
      setTimeout(() => setSuccess(null), 3000);
      setTimeout(() => loadRunningProcesses(), 1000);
    } catch (err) {
      setError('Ошибка публикации статистики в VK Ord');
      console.error('Error publishing stats:', err);
    } finally {
      setPublishing(false);
    }
  };

  const handleCollectVkStats = async () => {
    try {
      setCollectingVkStats(true);
      setError(null);
      await schedulerApi.startVkStatsCollection();
      setSuccess('Сбор просмотров для VK Video запущен');
      setTimeout(() => setSuccess(null), 3000);
      setTimeout(() => loadRunningProcesses(), 1000);
    } catch (err) {
      setError('Ошибка запуска сбора просмотров для VK Video');
      console.error('Error collecting VK stats:', err);
    } finally {
      setCollectingVkStats(false);
    }
  };
  const handleCollectYouTubeStats = async () => {
    try {
      setCollectingYouTubeStats(true);
      setError(null);
      await schedulerApi.startYouTubeStatsCollection();
      setSuccess('Сбор просмотров для YouTube запущен');
      setTimeout(() => setSuccess(null), 3000);
      setTimeout(() => loadRunningProcesses(), 1000);
    } catch (err) {
      setError('Ошибка запуска сбора просмотров для YouTube');
      console.error('Error collecting YouTube stats:', err);
    } finally {
      setCollectingYouTubeStats(false);
    }
  };

  const handleCollectRutubeStats = async () => {
    try {
      setCollectingRutubeStats(true);
      setError(null);
      await schedulerApi.startRutubeStatsCollection();
      setSuccess('Сбор просмотров для Rutube запущен');
      setTimeout(() => setSuccess(null), 3000);
      setTimeout(() => loadRunningProcesses(), 1000);
    } catch (err) {
      setError('Ошибка запуска сбора просмотров для Rutube');
      console.error('Error collecting Rutube stats:', err);
    } finally {
      setCollectingRutubeStats(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!config) {
    return <Alert severity="error">Не удалось загрузить конфигурацию</Alert>;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1.5 }}>
        Управление задачами
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1.5 }}>{success}</Alert>}

      <Box display="flex" flexDirection="column" gap={2}>
        <Accordion disableGutters defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="h6">Каналы для парсинга</Typography>
              <Chip size="small" label={channels.length} />
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={`вкл. ${channels.filter((c) => c.enabled).length}`}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              YouTube: <code>@handle</code> / UC… / URL. Shorts тоже собираются.
              ORD pad id — id площадки из ОРД (можно выбрать из списка площадок кабинета).
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" alignItems="flex-start" mb={2}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Платформа</InputLabel>
                <Select
                  label="Платформа"
                  value={newChannelPlatform}
                  onChange={(e) => setNewChannelPlatform(e.target.value as 'YOUTUBE' | 'VK_VIDEO' | 'RUTUBE')}
                >
                  <MenuItem value="YOUTUBE">YouTube</MenuItem>
                  <MenuItem value="VK_VIDEO">VK Video</MenuItem>
                  <MenuItem value="RUTUBE">Rutube</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label={
                  newChannelPlatform === 'YOUTUBE'
                    ? '@handle / UC… / URL'
                    : newChannelPlatform === 'RUTUBE'
                      ? 'Rutube channel id / URL'
                      : 'Owner id (−123…)'
                }
                value={newChannelId}
                onChange={(e) => setNewChannelId(e.target.value)}
                sx={{ minWidth: 220, flex: 1 }}
              />
              <TextField
                size="small"
                label="Название"
                value={newChannelName}
                onChange={(e) => setNewChannelName(e.target.value)}
                sx={{ minWidth: 160 }}
              />
              <TextField
                size="small"
                label="ORD pad id (опц.)"
                value={newChannelPadId}
                onChange={(e) => setNewChannelPadId(e.target.value)}
                sx={{ minWidth: 180 }}
                helperText="Из ОРД → площадки"
              />
              <Button
                variant="contained"
                onClick={handleAddChannel}
                disabled={addingChannel}
                sx={{ height: 40 }}
              >
                {addingChannel ? <CircularProgress size={18} /> : 'Добавить'}
              </Button>
            </Box>
            {channelsLoading ? (
              <CircularProgress size={22} />
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Платформа</TableCell>
                      <TableCell>Название</TableCell>
                      <TableCell>Channel id</TableCell>
                      <TableCell>ORD pad</TableCell>
                      <TableCell>Вкл</TableCell>
                      <TableCell align="right">Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {channels.map((ch) => (
                      <TableRow key={ch.id}>
                        <TableCell>{ch.platform}</TableCell>
                        <TableCell>{ch.name}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{ch.channelId}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{ch.platformExternalId}</TableCell>
                        <TableCell>
                          <Switch
                            size="small"
                            checked={ch.enabled}
                            onChange={() => handleToggleChannel(ch)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            startIcon={syncingChannelId === ch.id ? <CircularProgress size={14} /> : <PlayIcon />}
                            onClick={() => handleSyncChannel(ch.id)}
                            disabled={syncingChannelId !== null || !ch.enabled}
                          >
                            Собрать
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDeleteChannel(ch.id)}
                          >
                            Удалить
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {channels.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6}>Пока нет каналов</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>

        {/* VK Video Sync */}
        <Box display="flex" gap={2} flexWrap="wrap">
          <Card sx={{ flex: 1, minWidth: { xs: '100%', sm: 280 } }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Сбор видео</Typography>
                <Chip 
                  label={config.videoCollectionEnabled ? 'Включено' : 'Отключено'} 
                  color={config.videoCollectionEnabled ? 'success' : 'default'}
                />
              </Box>
              <TextField
                fullWidth
                label="Cron выражение"
                value={config.videoCollectionCron}
                margin="normal"
                size="small"
                InputProps={{ readOnly: true }}
                helperText="Изменение расписания доступно только через конфиг на сервере"
              />
              <Box display="flex" gap={2} mt={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  startIcon={<PlayIcon />}
                  onClick={() => startTask('youtube-sync')}
                  sx={{ flex: 1, minWidth: 160 }}
                >
                  Сбор видео YouTube
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PlayIcon />}
                  onClick={() => startTask('vk-video-sync')}
                  sx={{ flex: 1, minWidth: 160 }}
                >
                  Сбор видео VK
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PlayIcon />}
                  onClick={() => startTask('rutube-sync')}
                  sx={{ flex: 1, minWidth: 160 }}
                >
                  Сбор видео Rutube
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Stats Collection */}
        <Box display="flex" gap={2} flexWrap="wrap">
          <Card sx={{ flex: 1, minWidth: { xs: '100%', sm: 280 } }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Сбор просмотров</Typography>
                <Chip 
                  label={config.statsCollectionEnabled ? 'Включено' : 'Отключено'} 
                  color={config.statsCollectionEnabled ? 'success' : 'default'}
                />
              </Box>
              
              <TextField
                fullWidth
                label="Cron выражение"
                value={config.statsCollectionCron}
                margin="normal"
                size="small"
                InputProps={{ readOnly: true }}
                helperText="Изменение расписания доступно только через конфиг на сервере"
              />
              
              <Box display="flex" gap={2} mt={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  startIcon={<PlayIcon />}
                  onClick={handleCollectYouTubeStats}
                  sx={{ flex: 1, minWidth: 160 }}
                  disabled={collectingYouTubeStats}
                >
                  {collectingYouTubeStats ? <CircularProgress size={20} /> : 'Просмотры YouTube'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PlayIcon />}
                  onClick={handleCollectVkStats}
                  sx={{ flex: 1, minWidth: 160 }}
                  disabled={collectingVkStats}
                >
                  {collectingVkStats ? <CircularProgress size={20} /> : 'Просмотры VK'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PlayIcon />}
                  onClick={handleCollectRutubeStats}
                  sx={{ flex: 1, minWidth: 160 }}
                  disabled={collectingRutubeStats}
                >
                  {collectingRutubeStats ? <CircularProgress size={20} /> : 'Просмотры Rutube'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* VKord Sync */}
        <Box display="flex" gap={2} flexWrap="wrap">
          <Card sx={{ flex: 1, minWidth: { xs: '100%', sm: 280 } }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Публикация статистики в VK Ord</Typography>
                <Chip 
                  label={config.vkordSyncEnabled ? 'Включено' : 'Отключено'} 
                  color={config.vkordSyncEnabled ? 'success' : 'default'}
                />
              </Box>
              
              <TextField
                fullWidth
                label="Cron выражение"
                value={config.vkordSyncCron}
                margin="normal"
                size="small"
                InputProps={{ readOnly: true }}
                helperText="Изменение расписания доступно только через конфиг на сервере"
              />
              
              <Box
                display="grid"
                gap={2}
                mt={2}
                gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }}
                alignItems="flex-start"
              >
                <TextField
                  label="Дата начала отчётности (включительно)"
                  type="date"
                  value={startDt}
                  onChange={e => setStartDt(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="Дата конца отчётности (не включительно)"
                  type="date"
                  value={endDt}
                  onChange={e => setEndDt(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  fullWidth
                  helperText="Дата не включается"
                />
                <FormControl size="small" fullWidth>
                  <InputLabel>Платформа</InputLabel>
                  <Select
                    value={vkordPlatform}
                    label="Платформа"
                    onChange={e => setVkordPlatform(e.target.value)}
                  >
                    <MenuItem value="ALL">Все платформы</MenuItem>
                    <MenuItem value="YOUTUBE">YouTube</MenuItem>
                    <MenuItem value="VK_VIDEO">VK Video</MenuItem>
                    <MenuItem value="RUTUBE">Rutube</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box
                display="grid"
                gap={2}
                mt={2}
                gridTemplateColumns={{ xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}
              >
                <TextField
                  label="ERID (через запятую)"
                  value={vkordErids}
                  onChange={e => setVkordErids(e.target.value)}
                  placeholder="2Vtz..., 2Vtz..."
                  size="small"
                  fullWidth
                />
                <TextField
                  label="externalVideoId (через запятую)"
                  value={vkordExternalVideoIds}
                  onChange={e => setVkordExternalVideoIds(e.target.value)}
                  placeholder="videoId1, videoId2"
                  size="small"
                  fullWidth
                />
              </Box>
              <Button
                variant="contained"
                startIcon={<PlayIcon />}
                onClick={handleVkordSend}
                sx={{ mt: 2 }}
                fullWidth
                disabled={publishing}
              >
                {publishing ? <CircularProgress size={20} /> : `Опубликовать сейчас (${vkordPlatform === 'YOUTUBE' ? 'YouTube' : vkordPlatform === 'VK_VIDEO' ? 'VK Video' : vkordPlatform === 'RUTUBE' ? 'Rutube' : 'все платформы'})`}
              </Button>
            </CardContent>
          </Card>
        </Box>

        {/* Запущенные процессы */}
        {runningProcesses.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Запущенные процессы ({runningProcesses.length})
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Процесс</TableCell>
                      <TableCell>Начало</TableCell>
                      <TableCell>Сообщение</TableCell>
                      <TableCell>Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {runningProcesses.map((process) => (
                      <TableRow key={process.backgroundProcessId}>
                        <TableCell>
                          <Chip 
                            label={process.processCd} 
                            color="info" 
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {process.startedTs ? formatAppDateTime(process.startedTs, true, '-', true) : '-'}
                        </TableCell>
                        <TableCell>
                          {process.message || '-'}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate(`/data-view?processId=${process.backgroundProcessId}`)}
                          >
                            Показать результаты
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Сохранение конфигурации */}
        {/*
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Сохранение настроек
            </Typography>
            <Button
              variant="contained"
              onClick={saveConfig}
              disabled={saving}
              sx={{ mr: 2 }}
            >
              {saving ? <CircularProgress size={20} /> : 'Сохранить конфигурацию'}
            </Button>
            <Button
              variant="outlined"
              onClick={loadConfig}
              disabled={saving}
            >
              Отменить изменения
            </Button>
          </CardContent>
        </Card>
        */}
      </Box>
    </Box>
  );
};

export default TaskManagement;
